import { useEffect, useRef, useState } from 'react';
import { KeyboardData, useKeyboardStore } from './stores/keyboardStore';
import { MouseData, useMouseStore } from './stores/mouseStore';
import { UIData, useUIStore } from './stores/uiStore';
import { useScene } from '../context/sceneContext';

export const mouseHooks = () => {
  const scene = useScene();
  const [mouseData] = useMouseStore<MouseData>();
  const [keyboardData] = useKeyboardStore<KeyboardData>();
  const [UIData, setUIData] = useUIStore<UIData>();

  const [wasDragging, setWasDragging] = useState(false);

  useEffect(() => {
    if (mouseData.isDown) {
      setWasDragging(false);
    }
    if (mouseData.isDragging) {
      setWasDragging(true);
    }
  }, [mouseData.isDragging, mouseData.isDown]);

  // Scene offset
  useEffect(() => {
    if (mouseData.button === 1 && mouseData.isDragging) {
      setUIData({ cursor: 'move' });
    } else {
      setUIData({ cursor: 'default' });
    }
  }, [mouseData.isDragging]);

  useEffect(() => {
    if (mouseData.isDragging && (mouseData.button === 1 || mouseData.button === 2)) {
      // Use a single setUIData call with computed values
      const newX = UIData.sceneOffset.x + mouseData.movement.x;
      const newY = UIData.sceneOffset.y + mouseData.movement.y;
      setUIData({
        sceneOffset: { x: newX, y: newY },
      });
    }
  }, [mouseData.pos, mouseData.isDragging]);

  // Select elements
  useEffect(() => {
    if (UIData.editorMode === 'simulation') return;
    if (mouseData.hoveredUIElement === 'elementTreeNode') return;

    const elementID = mouseData.hoveredLauncherElementID;

    if (!elementID) {
      if (mouseData.button === 0 && !mouseData.isDragging && mouseData.target?.id === 'scene') {
        scene.deselectAllElements();
        setUIData({ currentLauncherState: [null, null] });
        scene.endEditingElement(null);
      }
      return;
    }

    if ((mouseData.isDown && mouseData.button === 0) || (!mouseData.isDown && mouseData.button === 2 && !wasDragging)) {
      if (UIData.selectedElementIDs.includes(elementID)) {
        scene.deselectElement(elementID);
      } else {
        scene.endEditingElement(null);

        if (keyboardData.keysDown.includes('ShiftLeft')) {
          scene.selectElement(elementID);
        } else {
          scene.selectOneElement(elementID);
        }

        setUIData({ currentLauncherState: [null, null] });
      }
    }
  }, [mouseData.isDown, wasDragging]);

  // Hover element
  useEffect(() => {
    if ((UIData.editorMode === 'simulation') === true) return;

    const elementID = mouseData.hoveredLauncherElementID;
    if (!elementID) {
      scene.unhoverAllElements();
      return;
    }

    scene.hoverOneElement(elementID);
  }, [mouseData.hoveredLauncherElementID]);

  // Detect doubleclick
  const onDoubleClick = (id: string | null, type?: string | null) => {
    if (mouseData.hoveredUIElement === 'elementTreeNode') return;
    
    if (!id || !type) return;
    if (type === 'text' || type === 'link') { scene.startEditingElement(id); scene.selectElement(id); };
  };

  const clickTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastClickTarget = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const handleClickTimer = () => {
      lastClickTarget.current = mouseData.target;

      clickTimer.current = setTimeout(() => {
        clickTimer.current = undefined;
        lastClickTarget.current = null;
      }, 300);
    };

    if (mouseData.isDown) {
      if (clickTimer.current && lastClickTarget.current === mouseData.target) {
        clearTimeout(clickTimer.current);
        clickTimer.current = undefined;
        lastClickTarget.current = null;
        onDoubleClick(mouseData.hoveredLauncherElementID, mouseData.hoveredLauncherElementType);
      } else {
        handleClickTimer();
      }
    }
  }, [mouseData.isDown, mouseData.target, mouseData.hoveredLauncherElementID, mouseData.hoveredLauncherElementType]);
};
